/**
 * Self-serve signup — the two marketplace account types, mutually exclusive:
 *
 *   Software provider → a companies tenant (integrates locks via the API,
 *                       buys and deploys hardware).
 *   Hardware provider → a manufacturers tenant (lists boards, sets prices,
 *                       manages public product pages).
 *
 * Creates the organization plus its first admin user and signs them in.
 * Mirrors the Login page's look.
 */
import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { IconArrowLeft, IconPlugConnected, IconCpu } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadTurnstile(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile failed to load')));
      return;
    }
    const s = document.createElement('script');
    s.src = TURNSTILE_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile failed to load'));
    document.head.appendChild(s);
  });
}
import type { SignupData } from '../context/AuthContext';
import { FormGroup, Label, Input, ErrorAlert, HelperText } from '../components/styled';

// ── Layout (matches Login.tsx) ────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(6)};
  background: ${({ theme }) => theme.background.secondary};
`;

const Wrap = styled.div`
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-decoration: none;
  transition: color 0.1s ease;

  &:hover {
    color: ${({ theme }) => theme.font.color.secondary};
  }
`;

const Card = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  box-shadow: 0 10px 40px rgba(13, 37, 61, 0.08);
  padding: clamp(28px, 5vw, 40px);
`;

const Brand = styled.h1`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: -0.3px;
  color: ${({ theme }) => theme.font.color.primary};
  text-align: center;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-align: center;
  margin: ${({ theme }) => theme.spacing(1)} 0 ${({ theme }) => theme.spacing(6)} 0;
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TypeCard = styled.button<{ selected: boolean }>`
  text-align: left;
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  border: 2px solid ${({ theme, selected }) => selected ? theme.accent.primary : theme.border.color.medium};
  background: ${({ theme, selected }) => selected ? theme.accent.quaternary ?? theme.background.secondary : theme.background.primary};
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.family};
  transition: border-color 0.1s ease;

  &:hover {
    border-color: ${({ theme }) => theme.accent.primary};
  }

  svg {
    color: ${({ theme }) => theme.accent.primary};
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }
`;

const TypeTitle = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

const TypeBlurb = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
  margin-top: ${({ theme }) => theme.spacing(1)};
  line-height: 1.4;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 44px;
  background: ${({ theme }) => theme.accent.primary};
  color: ${({ theme }) => theme.font.color.inverted};
  border: none;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  font-size: ${({ theme }) => theme.font.size.md};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-family: ${({ theme }) => theme.font.family};
  cursor: pointer;
  transition: background 0.1s ease, transform 0.15s ease;
  margin-top: ${({ theme }) => theme.spacing(4)};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.accent.accent10};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoginRow = styled.p`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-align: center;
  margin: ${({ theme }) => theme.spacing(4)} 0 0;

  a {
    color: ${({ theme }) => theme.accent.primary};
    text-decoration: none;
    font-weight: ${({ theme }) => theme.font.weight.medium};

    &:hover {
      text-decoration: underline;
    }
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

function Signup() {
  const [searchParams] = useSearchParams();
  const [accountType, setAccountType] = useState<SignupData['accountType']>(
    searchParams.get('type') === 'hardware' ? 'hardware' : 'software'
  );
  const [organizationName, setOrganizationName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  // Captcha: the API says whether one is required and which site key to use.
  const [captchaSiteKey, setCaptchaSiteKey] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaEl = useRef<HTMLDivElement | null>(null);
  const captchaWidget = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ captcha: { provider: string; siteKey: string } | null }>('/auth/signup-config')
      .then((res) => { if (!cancelled) setCaptchaSiteKey(res.data.captcha?.siteKey || null); })
      .catch(() => { /* no config = no captcha; the API will still refuse if it requires one */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!captchaSiteKey || !captchaEl.current || captchaWidget.current) return;
    let cancelled = false;
    loadTurnstile().then(() => {
      if (cancelled || !window.turnstile || !captchaEl.current || captchaWidget.current) return;
      captchaWidget.current = window.turnstile.render(captchaEl.current, {
        sitekey: captchaSiteKey,
        callback: (token: string) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(''),
        'error-callback': () => setCaptchaToken(''),
      });
    }).catch(() => setError('The verification widget could not be loaded. Please refresh and try again.'));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaSiteKey, sentTo]);

  function resetCaptcha() {
    setCaptchaToken('');
    if (window.turnstile && captchaWidget.current) window.turnstile.reset(captchaWidget.current);
  }
  const { signup } = useAuth();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await signup({ accountType, organizationName, name, email, password, ...(captchaSiteKey ? { captchaToken } : {}) });
      // No session yet — the account activates via the emailed link.
      setSentTo(result.email);
    } catch (err: any) {
      setError(
        err.response?.data?.error
        || err.response?.data?.errors?.[0]?.msg
        || 'Signup failed'
      );
      if (captchaSiteKey) resetCaptcha(); // tokens are single-use
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResent(false);
    try {
      await (await import('../services/api')).default.post('/auth/resend-verification', { email: sentTo });
      setResent(true);
    } catch {
      setResent(true); // Endpoint is deliberately generic — treat as sent.
    }
  }

  if (sentTo) {
    return (
      <Page>
        <Wrap>
          <Card>
            <Brand>Simkura</Brand>
            <Subtitle>Check your email</Subtitle>
            <p style={{ textAlign: 'center', lineHeight: 1.5 }}>
              We sent a verification link to <strong>{sentTo}</strong>.
              Click it to activate your account and sign in — you can't sign
              in until your email is verified.
            </p>
            <HelperText style={{ display: 'block', textAlign: 'center', marginTop: 12 }}>
              The link expires in 24 hours.
            </HelperText>
            <SubmitButton type="button" onClick={handleResend}>
              {resent ? 'Sent — check again' : 'Resend the email'}
            </SubmitButton>
            <LoginRow>
              Wrong address? <Link to="/signup" onClick={() => setSentTo(null)}>Start over</Link>
            </LoginRow>
          </Card>
          <BackLink to="/">
            <IconArrowLeft size={15} /> Back to home
          </BackLink>
        </Wrap>
      </Page>
    );
  }

  return (
    <Page>
      <Wrap>
        <Card>
          <Brand>Simkura</Brand>
          <Subtitle>Create your account</Subtitle>
          <form onSubmit={handleSubmit}>
            {error && <ErrorAlert>{error}</ErrorAlert>}

            <TypeGrid>
              <TypeCard
                type="button"
                selected={accountType === 'software'}
                onClick={() => setAccountType('software')}
                aria-pressed={accountType === 'software'}
              >
                <IconPlugConnected size={20} stroke={1.8} />
                <TypeTitle>Software provider</TypeTitle>
                <TypeBlurb>
                Integrate you access control platform to connect to any Simkura enabled lock
                </TypeBlurb>
              </TypeCard>
              <TypeCard
                type="button"
                selected={accountType === 'hardware'}
                onClick={() => setAccountType('hardware')}
                aria-pressed={accountType === 'hardware'}
              >
                <IconCpu size={20} stroke={1.8} />
                <TypeTitle>Hardware provider</TypeTitle>
                <TypeBlurb>
                  Sell your hardware on the platform — list boards, set
                  pricing, manage your public product pages.
                </TypeBlurb>
              </TypeCard>
            </TypeGrid>
            <HelperText style={{ marginTop: -8, marginBottom: 16, display: 'block' }}>
              An account is one or the other — this can't be changed later.
            </HelperText>

            <FormGroup>
              <Label htmlFor="organizationName">
                {accountType === 'hardware' ? 'Company (hardware provider) name' : 'Company (software provider) name'}
              </Label>
              <Input
                id="organizationName"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder={accountType === 'hardware' ? 'e.g. Mercury Boards' : 'e.g. Acme Access'}
                required
                minLength={2}
                maxLength={150}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <HelperText>At least 8 characters.</HelperText>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </FormGroup>

            {captchaSiteKey && (
              <div ref={captchaEl} style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }} />
            )}
            <SubmitButton type="submit" disabled={loading || (!!captchaSiteKey && !captchaToken)}>
              {loading ? 'Creating account…' : 'Create account'}
            </SubmitButton>
            <LoginRow>
              Already have an account? <Link to="/login">Sign in</Link>
            </LoginRow>
          </form>
        </Card>
        <BackLink to="/">
          <IconArrowLeft size={15} /> Back to home
        </BackLink>
      </Wrap>
    </Page>
  );
}

export default Signup;
